import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useState } from 'react'
import DotValueCell from 'core/components/listTable/cells/DotValueCell'
import ListTableField from 'core/components/validatedForm/ListTableField'
import Badge from 'core/elements/badge'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { humanReadableSize } from 'openstack/helpers'
import { getVirtualMachine, updateVmVolumeTermination } from './actions'
import { VirtualMachine } from './newModel'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}))

const NameCell = ({ name, item }: { name: string; item: any }) => {
  const classes = useStyles()
  return (
    <div className={classes.nameCell}>
      <Text variant="body2">{name || item?.id}</Text>{' '}
      {item.boot_index === 0 && <Badge variant="primary" text="Root Disk Volume" ellipsisAt={20} />}
    </div>
  )
}

const volumeColumns = [
  {
    id: 'name',
    label: 'Name',
    render: (name, item) => {
      return <NameCell name={name} item={item} />
    },
  },
  {
    id: 'id',
    label: 'ID',
  },
  {
    id: 'device_name',
    label: 'Device',
  },
  {
    id: 'volume_size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'bootable',
    label: 'Bootable',
    render: (val) => <DotValueCell value={val} />,
  },
]

const defaultParams = {
  selected: [],
}

export const getVolumesToEnableAndDisable = ({ updatedVm, params }) => {
  const currentEnabledVolumes = Object.values(updatedVm?.volumeDetails || {})
    .filter((volume: any) => !!volume?.delete_on_termination)
    .map((volume: any) => volume.id)
  const desiredEnabledVolumes = params.selected.map((volume: any) => volume.id)
  const enableVolumes = desiredEnabledVolumes.filter(
    (id: string) => !currentEnabledVolumes.includes(id),
  )
  const disableVolumes = currentEnabledVolumes.filter(
    (id: string) => !desiredEnabledVolumes.includes(id),
  )
  return { enableVolumes, disableVolumes }
}

export default function ManageVolumeTerminationModal({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const [updatedVm, setUpdatedVm] = useState<VirtualMachine | null>(null)
  const [volumes, setVolumes] = useState<any[]>([])

  const { params, getParamsUpdater, updateParams, setParams } = useParams<{
    selected: any[]
  }>(defaultParams)

  const {
    update: updateVolumeTermination,
    updating: updatingVolumeTermination,
    error: updateVolumeTerminationError,
    reset: updateVolumeTerminationReset,
  } = useUpdateAction(updateVmVolumeTermination)

  // Get updated attachment info -- full data only available from CQRS
  // but do not want to update cached VM data, so use standalone call
  useEffect(() => {
    const getUpdatedVm = async () => {
      try {
        const currentVm = await getVirtualMachine({ id: vm.id })
        setUpdatedVm(currentVm)
      } catch (e) {
        setUpdatedVm(vm)
      }
    }
    getUpdatedVm()
  }, [vm?.id])

  useEffect(() => {
    if (updatedVm) {
      const volumesList = Object.values(updatedVm?.volumeDetails || {})
      setVolumes(volumesList)
      setParams({
        selected: volumesList.filter((volume) => !!volume?.delete_on_termination),
      })
    }
  }, [updatedVm])

  const handleOnSubmit = useCallback(async () => {
    const { enableVolumes, disableVolumes } = getVolumesToEnableAndDisable({ updatedVm, params })
    const { success } = await updateVolumeTermination({
      id: vm?.id,
      enableVolumeTerm: enableVolumes,
      disableVolumeTerm: disableVolumes,
    })
    if (success) {
      onClose(true)
      updateVolumeTerminationReset()
    }
  }, [params, updatedVm, vm?.id])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    updateVolumeTerminationReset()
    onClose(success)
  }
  return (
    <ModalForm
      open
      title={`Enable Volume Termination`}
      entityName={vm?.name}
      onSubmit={handleOnSubmit}
      onClose={handleClose}
      submitting={updatingVolumeTermination}
      error={updateVolumeTerminationError}
      submitTitle={`Save Changes`}
      maxWidth={800}
      closeText="Cancel"
    >
      <div className={classes.root}>
        <Text variant="body2">
          Enable volume termination to automatically delete attached volumes when the VM is deleted.
          Disable to keep the volumes and make them available for reuse.
        </Text>
        <ListTableField
          id="selected"
          data={volumes}
          loading={!updatedVm}
          columns={volumeColumns}
          onChange={(value) =>
            updateParams({
              selected: value,
            })
          }
          value={params.selected}
          uniqueIdentifier="id"
          searchTargets={['name', 'id', 'device_name', 'volume_size']}
          multiSelection
          useToggleSwitch
          noScroll
          emptyText={'No volumes were found on this virtual machine.'}
        />
      </div>
    </ModalForm>
  )
}
